import React, { useState } from 'react'
import {
  FiCreditCard,
  FiDollarSign,
  FiChevronUp,
  FiChevronDown,
  FiTrendingUp,
  FiPercent,
  FiRepeat,
  FiActivity,
} from 'react-icons/fi'
import { useHistory } from 'react-router-dom'
import {
  BUYER_ORDER_UNPAID,
  BUYER_ORDER_PAID,
  HOST_ORDER_VERIFIED,
  HOST_ORDER_COLLECTED,
  HOST_ORDER_SUBMITTED_VERIFIED,
  HOST_ORDER_SUBMITTED_COLLECTED,
} from '_utils/constant'
import { formatCurrency } from '../helper/formatNumber'
import useDashboardRedirect from '../hooks/useDashboardRedirect'

export default function Overview({
  summaryReport,
  hitpayReport,
  loading,
  filters,
  isViewAll,
  eventTimeRange,
}) {
  const history = useHistory()
  const { redirectToManageOrders } = useDashboardRedirect()
  const [overviewType, setOverviewType] = useState('general')

  const handleCardClick = (card) => {
    if (loading || !card.path || isViewAll) return
    if (card.path === '/manage-orders') {
      redirectToManageOrders({
        dateRangeOrder: card.navigationState?.dateRange,
        paymentStatus: card.navigationState?.paymentStatus,
        dateRangeEvent: eventTimeRange,
        eventId: filters?.eid,
        status: filters?.status,
      })
    } else {
      history.push(card.path, card.navigationState)
    }
  }

  const generalCards = [
    {
      id: 'total-sales',
      title: 'Total Sales',
      value: formatCurrency(summaryReport?.totalSales),
      icon: <FiTrendingUp />,
      trendIcon: <FiChevronUp className='trend-arrow' />,
      theme: 'teal',
      path: '/manage-orders',
      navigationState: {
        fromDashboard: true,
        dateRange: { start: filters?.start, end: filters?.end },
        paymentStatus: [],
      },
    },
    {
      id: 'sales-collected',
      title: 'Sales Collected',
      value: formatCurrency(summaryReport?.revenueCollection ?? summaryReport?.RevenueCollection),
      icon: <FiDollarSign />,
      trendIcon: <FiChevronUp className='trend-arrow' />,
      theme: 'green',
      path: '/manage-orders',
      navigationState: {
        fromDashboard: true,
        dateRange: { start: filters?.start, end: filters?.end },
        paymentStatus: [
          { value: HOST_ORDER_VERIFIED, label: 'Verified', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_VERIFIED },
          { value: HOST_ORDER_COLLECTED, label: 'Collected', type: 'host', includeSubmitted: HOST_ORDER_SUBMITTED_COLLECTED },
        ],
      },
    },
    {
      id: 'payment-outstanding',
      title: 'Payment Outstanding',
      value: summaryReport?.pendingPayments ?? 0,
      icon: <FiCreditCard />,
      trendIcon: <FiChevronDown className='trend-arrow' />,
      theme: 'red',
      path: '/manage-orders',
      navigationState: {
        fromDashboard: true,
        dateRange: { start: filters?.start, end: filters?.end },
        paymentStatus: [
          { value: BUYER_ORDER_UNPAID, label: 'Not Paid' },
          { value: BUYER_ORDER_PAID, label: 'Pending Review', isPending: true },
        ],
      },
    },
    {
      id: 'total-profit',
      title: 'Total Profit',
      value: formatCurrency(summaryReport?.eventProfit),
      icon: (
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round'>
          <circle cx='12' cy='12' r='10' />
          <path d='M9.5 9a2.5 2.5 0 0 1 2.5-2.5h.5a2.5 2.5 0 0 1 0 5H12a2.5 2.5 0 0 0 0 5h.5a2.5 2.5 0 0 0 2.5-2.5' />
          <line x1='12' y1='5' x2='12' y2='7' />
          <line x1='12' y1='17' x2='12' y2='19' />
        </svg>
      ),
      trendIcon: <FiChevronUp className='trend-arrow' />,
      theme: 'blue',
      path: null,
    },
  ]

  const hitpayCards = [
    {
      id: 'hitpay-total-amount',
      title: 'Total HitPay Sales',
      value: formatCurrency(hitpayReport?.totalAmount),
      icon: <FiTrendingUp />,
      theme: 'teal',
      path: null,
    },
    {
      id: 'hitpay-net-received',
      title: 'Net Amount Received',
      value: formatCurrency(hitpayReport?.netAmountReceived),
      icon: <FiDollarSign />,
      theme: 'green',
      path: null,
    },
    {
      id: 'hitpay-transactions',
      title: 'Transactions',
      value: hitpayReport?.transactionCount ?? 0,
      icon: <FiRepeat />,
      theme: 'blue',
      path: null,
    },
    {
      id: 'hitpay-total-fee',
      title: 'HitPay Fee',
      value: formatCurrency(hitpayReport?.totalHitpayFee),
      icon: <FiPercent />,
      theme: 'orange',
      path: null,
    },
    {
      id: 'hitpay-commission',
      title: 'Commission',
      value: formatCurrency(hitpayReport?.totalCommission),
      icon: <FiActivity />,
      theme: 'red',
      path: null,
    },
  ]

  const displayCards = overviewType === 'hitpay' ? hitpayCards : generalCards

  return (
    <div className='overview-container mb-3'>
      <div className='overview-header-bar d-flex align-items-center justify-content-between mb-2'>
        <div className='section-label mb-0'>
          {overviewType === 'general' ? 'Overview' : 'HitPay Payment Overview'}
        </div>
        <div className='overview-toggle-switch'>
          <button
            type='button'
            className={`toggle-btn ${overviewType === 'general' ? 'active' : ''}`}
            onClick={() => setOverviewType('general')}
          >
            General Overview
          </button>
          <button
            type='button'
            className={`toggle-btn ${overviewType === 'hitpay' ? 'active' : ''}`}
            onClick={() => setOverviewType('hitpay')}
          >
            HitPay Payment
          </button>
        </div>
      </div>
      <div className={`overview-wrapper ${overviewType === 'hitpay' ? 'hitpay-mode' : ''}`}>
        {displayCards.map((card) => (
          <div
            key={card.id}
            className={`overview-card theme-${card.theme} ${isViewAll || !card.path ? 'disabled-card' : ''}`}
            onClick={() => handleCardClick(card)}
            role='button'
            tabIndex={0}
          >
            <div className='card-icon-container'>{card.icon}</div>
            <div className='card-content'>
              <span className='card-title'>{card.title}</span>
              <h3 className='card-value'>
                {loading ? <span className='skeleton-loader' /> : card.value ?? 0}
              </h3>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
